import {formatDateOnly} from "@/shared/helpers/dates";

/**
 * Builds disabled dates array for v-calendar based on schedule timeslots and exceptions.
 * Handles the case where a weekday has no recurring availability but has exceptions with timeslots.
 *
 * @param {Array} exceptions - Array of exception objects with {date, timeslots}
 * @param {Array} timeslots - Array of recurring timeslot objects with {weekday}
 * @param {number} daysAdvance - Max days in the future to consider (default 30)
 * @returns {Array} Disabled dates array compatible with v-calendar's disabled-dates prop
 */
export function buildScheduleDisabledDates(exceptions, timeslots, daysAdvance = 30) {
	let result = [];

	// 1. Collect exception dates that HAVE timeslots (these should be enabled)
	let enabledExceptionDates = new Set();
	if(exceptions) {
		for(let exception of exceptions) {
			if(!exception.timeslots || exception.timeslots.length === 0) {
				result.push(new Date(exception.date));
			} else {
				enabledExceptionDates.add(exception.date);
			}
		}
	}

	// 2. Find weekdays without recurring timeslots
	let emptyWeekdays = [0, 1, 2, 3, 4, 5, 6];
	if(timeslots) {
		timeslots.forEach(t => {
			if(t.weekday !== null) {
				emptyWeekdays = emptyWeekdays.filter(d => d !== (t.weekday + 1) % 7);
			}
		});
	}

	// 3. Check which empty weekdays have exceptions with timeslots
	let weekdaysWithExceptions = new Set();
	for(let exceptionDate of enabledExceptionDates) {
		let d = new Date(exceptionDate);
		let weekday = d.getDay();
		if(emptyWeekdays.includes(weekday)) {
			weekdaysWithExceptions.add(weekday);
		}
	}

	// 4. For weekdays without exceptions: use repeat rule
	let weekdaysForRepeat = emptyWeekdays.filter(w => !weekdaysWithExceptions.has(w));
	if(weekdaysForRepeat.length > 0) {
		result.push({
			repeat: {
				weekdays: weekdaysForRepeat.map(d => d + 1)
			}
		});
	}

	// 5. For weekdays that have exceptions, enumerate individual dates (excluding exceptions)
	if(weekdaysWithExceptions.size > 0) {
		let today = new Date();
		today.setHours(0, 0, 0, 0);

		for(let i = 0; i <= daysAdvance; i++) {
			let date = new Date(today);
			date.setDate(date.getDate() + i);
			let weekday = date.getDay();

			if(weekdaysWithExceptions.has(weekday)) {
				let dateStr = formatDateOnly(date);
				if(!enabledExceptionDates.has(dateStr)) {
					result.push(new Date(date));
				}
			}
		}
	}

	return result;
}
